import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Typography } from '@material-ui/core';

import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import HotelOutlinedIcon from '@material-ui/icons/HotelOutlined';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

import { currencyFormatter } from 'utils/prices/formatter';
import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';

import useStyles from './styles';
import RentalPrices from './rental-prices/RentalPrices';

const _getPricePerDay = (item, currency, isAvailableEmptyPrice) => {
  const classes = useStyles();

  let price = item[currency];
  let selectedCurrency = currency;

  if (!parseFloat(item[currency])) {
    if (!isAvailableEmptyPrice && !parseFloat(price)) {
      ['NOK', 'EUR', 'USD'].map(currency => {
        if (!parseFloat(price) && item[currency]) {
          price = item[currency];
          selectedCurrency = currency;
        }
      });
    }
  }

  const value = currencyFormatter(price);

  return (
    <span className={classes.currency}>
      {' '}
      {selectedCurrency} {value}
    </span>
  );
};

const DailyPrices = ({
  days,
  isMultipleDays,
  initialSelectedCurrency,
  isAvailableEmptyPrice
}) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const isPricePerDay = !!(days?.[0]?.day && !days[0].categories);

  return (
    <div className={isPricePerDay ? classes.pricePerDayWrapper : null}>
      {days?.map((item, index, row) => {
        const days = isMultipleDays ? item.days : [item.day];

        if (item.categories) {
          return (
            <div key={index}>
              <Typography variant="h3">
                {days.map((day, index) => `${index ? ',' : ''} ${t(day)}`)}
              </Typography>
              {item.categories.map((category, index) => {
                const { price, currency } = getPrice(
                  category,
                  initialSelectedCurrency,
                  isAvailableEmptyPrice
                );
                const isLastItem = index === item.categories.length - 1;
                return (
                  <span key={index}>
                    {category.participant === 'Universal'
                      ? (category.participant = t('Person'))
                      : t(category.participant)}{' '}
                    <span className={classes.currency}>
                      {currency} {price}
                    </span>{' '}
                    {!isLastItem && `• `}
                  </span>
                );
              })}
            </div>
          );
        }

        const isLastItem = index === row.length - 1;

        if (item.day) {
          return (
            <div key={index}>
              <Typography variant="h3" className={classes.pricePerDayTitle}>
                {t(item.day)}:{' '}
              </Typography>
              {_getPricePerDay(
                item,
                initialSelectedCurrency,
                isAvailableEmptyPrice
              )}
              {!isLastItem && <span>&nbsp;•&nbsp;</span>}
            </div>
          );
        }
      })}
    </div>
  );
};

export const Prices = ({
  prices,
  pricesType,
  scheduleType,
  initialSelectedCurrency,
  isAvailableEmptyPrice
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const isAccommodation = !scheduleType;

  switch (pricesType) {
    case 'pricesFixedForm': {
      const { price, currency } = getPrice(
        prices,
        initialSelectedCurrency,
        isAvailableEmptyPrice
      );
      if (scheduleType === 'schedulePassForm' || isAccommodation) {
        return (
          <div className={classes.currency}>
            {currency} {price}
          </div>
        );
      } else {
        return (
          <div>
            {prices?.days?.map((item, index) => (
              <div key={index}>
                <Typography variant="h3">
                  {item.days.map(
                    (day, index) => `${index ? ',' : ''} ${t(day)}`
                  )}
                </Typography>
                <Typography variant="body1" className={classes.currency}>
                  {currency} <span>{currencyFormatter(item[currency])}</span>
                </Typography>
              </div>
            ))}
          </div>
        );
      }
    }
    case 'pricesDailyForm': {
      const days = prices?.days;
      return (
        <DailyPrices
          days={days}
          initialSelectedCurrency={initialSelectedCurrency}
          isAvailableEmptyPrice={isAvailableEmptyPrice}
        />
      );
    }
    case 'pricesDailyScheduleForm': {
      const days = prices?.daysArray;
      return (
        <DailyPrices
          days={days}
          isMultipleDays={true}
          initialSelectedCurrency={initialSelectedCurrency}
          isAvailableEmptyPrice={isAvailableEmptyPrice}
        />
      );
    }
    case 'pricesCategoryForm':
    case 'pricesEqualForm': {
      if (!prices?.categories?.length) return null;

      return (
        <div>
          {prices.categories.map((item, index) => {
            const { price, currency } = getPrice(
              item,
              initialSelectedCurrency,
              isAvailableEmptyPrice
            );
            const isLastItem = index === prices.categories.length - 1;
            return (
              <span key={index}>
                {item.participant === 'Universal'
                  ? (item.participant = t('Person'))
                  : t(item.participant)}
                :{' '}
                <span className={classes.currency}>
                  {currency} {price}
                </span>{' '}
                {!isLastItem && `• `}
              </span>
            );
          })}
        </div>
      );
    }
  }

  return null;
};

const getPrice = (prices, initialSelectedCurrency, isAvailableEmptyPrice) => {
  let initialCurrency = initialSelectedCurrency;
  let price = prices[initialSelectedCurrency];

  if (!isAvailableEmptyPrice && !parseFloat(price)) {
    ['NOK', 'EUR', 'USD'].map(currency => {
      if (!parseFloat(price) && prices[currency]) {
        price = prices[currency];
        initialCurrency = currency;
      }
    });
  }

  return {
    price: currencyFormatter(price),
    currency: initialCurrency
  };
};

const ProductShortInfo = ({
  item,
  initialSelectedCurrency,
  isPrices,
  isRentalPrices,
  isBedDetails,
  titleClass,
  isRightArrow,
  selectProduct
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const selectedLanguage = i18n.language;


  if (!item) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.loadingState}>
          {t('Loading product...')}
        </div>
      </div>
    );
  }

  const onSelectHandler = () => {

    const pricesArray = Array.isArray(item?.prices) ? item.prices : [];
    const hourlyPricesArray = Array.isArray(item?.hourlyPrices) ? item.hourlyPrices : [];
    
    const listOfAvailableCurrencies = getListOfAvailableCurrencies(
      pricesArray.length > 0 ? pricesArray : hourlyPricesArray
    );

    selectProduct?.({
      ...item,
      listOfAvailableCurrencies: listOfAvailableCurrencies
    });
  };

  const getFieldByCurrentLanguage = field => {
    if (!field || !item) return '';

    if (selectedLanguage === 'Norway' && item[`${field}No`]) {
      return item[`${field}No`] || '';
    }

    return item[field] || '';
  };
  const photoUrl = item?.photo || '';

  return (
    <div onClick={() => onSelectHandler()} className={classes.wrapper}>
      <div
        className={classes.photo}
        style={{ 
          backgroundImage: photoUrl ? `url(${photoUrl})` : 'none',
          backgroundColor: !photoUrl ? '#f5f5f5' : 'transparent'
        }}
      >
        {!photoUrl && <div className={classes.noPhoto}>No Image</div>}
      </div>

      <div className={classes.infoWrapper}>
        <div className={classes.titleBlock}>
          <Typography
            variant="h2"
            classes={{ root: classNames(classes.title, titleClass) }}
          >
            {getFieldByCurrentLanguage('title') || t('No title')}
          </Typography>
          {isRightArrow && (
            <ArrowForwardIosIcon className={classes.arrowRightIcon} />
          )}
        </div>
        
        {isPrices && item?.pricesType && (
          <div className={classes.prices}>
            <Prices
              prices={item[item.pricesType]}
              pricesType={item.pricesType}
              scheduleType={item?.scheduleType}
              initialSelectedCurrency={initialSelectedCurrency}
            />
          </div>
        )}

        {isRentalPrices && item?.pricesType && (
          <div className={classes.prices}>
            <RentalPrices
              prices={item.pricesType === 'pricesHourlyForm' ? 
                (item?.hourlyPrices || []) : 
                (item?.prices || [])
              }
              pricesType={item.pricesType}
              initialSelectedCurrency={initialSelectedCurrency}
              isAvailableEmptyPrice={false}
            />
          </div>
        )}

        
        <div>
        
          <div className={classes.detailsItem}>
            <LocationOnOutlinedIcon className={classes.detailsIcon} />
            <div className={classes.detailsInfo}>
              {item?.location || t('No location')}
            </div>
          </div>
          
          <div className={classes.detailsItem}>
            <PeopleOutlineIcon className={classes.detailsIcon} />
            <div className={classes.detailsInfo}>
              {item?.guestsNumber != null 
                ? `${item.guestsNumber} ${t('Guests')}`
                : `${item?.availableUnits} ${t('Units')}`
              }
            </div>
          </div>
          
          {isBedDetails && Array.isArray(item?.bedDetails) && item.bedDetails.length > 0 && (
            <div className={classes.detailsItem}>
              <HotelOutlinedIcon className={classes.detailsIcon} />
              <div className={classes.detailsInfo}>
                {item.bedDetails.join(' • ')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductShortInfo;
